import React from "react";
import {useAtom} from "jotai";
import {Box, Button, Modal, Stack, Typography} from "@mui/material";
import {keyframes} from "@emotion/react";
import {HugeiconsIcon} from "@hugeicons/react";
import {PhoneCallIcon, PhoneOff01Icon} from "@hugeicons/core-free-icons";
import {CallConfirmDialogAtom} from "../../pages/project/atoms";

const ringPulse = keyframes`
  0% { transform: scale(0.85); opacity: 0.55; }
  70% { transform: scale(1.55); opacity: 0; }
  100% { transform: scale(1.55); opacity: 0; }
`;

const popIn = keyframes`
  from { opacity: 0; transform: translate(-50%, -46%) scale(0.97); }
  to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`;

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "min(92vw, 380px)",
  bgcolor: "#fff",
  borderRadius: "28px",
  boxShadow: "0 24px 70px rgba(35, 38, 77, 0.18)",
  px: 3.5,
  py: 4,
  outline: "none",
  animation: `${popIn} 0.35s cubic-bezier(0.34, 1.3, 0.64, 1)`,
};

const ringSx = (delay) => ({
  position: "absolute",
  inset: 0,
  borderRadius: "50%",
  bgcolor: "rgba(47, 194, 117, 0.3)",
  animation: `${ringPulse} 2s ease-out infinite`,
  animationDelay: `${delay}s`,
});

const buttonSx = {
  flex: 1,
  height: 52,
  borderRadius: "16px",
  textTransform: "none",
  fontSize: 15,
  fontWeight: 600,
  transition: "background-color 0.2s ease, transform 0.2s ease",
  "&:active": {transform: "scale(0.97)"},
};

// Phone-call-style confirm: green answer button, red hang-up button, avatar with pulsing rings —
// pairs with useCallConfirm. Kept as its own atom/component so it never collides with the
// generic yes/no ConfirmDialogModal used elsewhere.
export const CallConfirmDialogModal = () => {
  const [dialog, setDialog] = useAtom(CallConfirmDialogAtom);

  const handleClose = () => {
    setDialog((prev) => ({...prev, open: false}));
  };

  const handleConfirm = () => {
    dialog.resolve(true);
    handleClose();
  };

  const handleCancel = () => {
    dialog.resolve(false);
    handleClose();
  };

  return (
    <Modal open={dialog.open} onClose={handleCancel}>
      <Stack alignItems="center" gap={0.5} sx={style}>
        <Box sx={{position: "relative", width: 84, height: 84, display: "grid", placeItems: "center", mb: 2}}>
          <Box aria-hidden sx={ringSx(0)} />
          <Box aria-hidden sx={ringSx(0.6)} />
          <Box
            sx={{
              position: "relative",
              width: 64,
              height: 64,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              bgcolor: "#D9F2E4",
              color: "#1E7F52",
            }}
          >
            <HugeiconsIcon icon={PhoneCallIcon} size={30} className="phone-shake" />
          </Box>
        </Box>

        <Typography align="center" fontWeight={700} fontSize="1.1rem" sx={{px: 1, color: "#23264D"}}>
          {dialog.title}
        </Typography>
        {dialog.subtitle && (
          <Typography align="center" fontSize="0.875rem" sx={{px: 1, mb: 1, color: "#6B6B80"}}>
            {dialog.subtitle}
          </Typography>
        )}

        <Stack direction="row" gap={1.5} sx={{mt: 3, width: "100%"}}>
          <Button
            onClick={handleCancel}
            startIcon={<HugeiconsIcon icon={PhoneOff01Icon} size={20} />}
            sx={{...buttonSx, color: "#374151", bgcolor: "#F2F2F7", "&:hover": {bgcolor: "#E6E6EF"}}}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            startIcon={<HugeiconsIcon icon={PhoneCallIcon} size={20} />}
            sx={{...buttonSx, color: "#fff", bgcolor: "#1E8A55", "&:hover": {bgcolor: "#187548"}}}
          >
            Call
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
};
